import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {TranscriptionJob} from '../../domain/types';
import {
  fetchQueue,
  startQueueProcessing,
  stopQueueProcessing,
  addToQueue,
  removeFromQueue,
} from './queueThunks';

interface QueueState {
    jobs: TranscriptionJob[];
    isProcessing: boolean;
    loading: boolean;
    error: string | null;
    liveOutput: string;
    queueCompletionStatus: string;
    processingOriginPath: string | null;
}

const initialState: QueueState = {
  jobs: [],
  isProcessing: false,
  loading: false,
  error: null,
  liveOutput: '',
  queueCompletionStatus: localStorage.getItem('queueCompletionStatus') || '',
  processingOriginPath: null,
};

const queueSlice = createSlice({
  name: 'queue',
  initialState,
  reducers: {
    setLiveOutput: (state, action: PayloadAction<string>) => {
      state.liveOutput = action.payload;
    },
    setQueueCompletionStatus: (state, action: PayloadAction<string>) => {
      state.queueCompletionStatus = action.payload;
      if (action.payload) {
        localStorage.setItem('queueCompletionStatus', action.payload);
      } else {
        localStorage.removeItem('queueCompletionStatus');
      }
    },
    clearCompletionStatus: (state) => {
      state.queueCompletionStatus = '';
      localStorage.removeItem('queueCompletionStatus');
    },
    updateJobInQueue: (state, action: PayloadAction<TranscriptionJob>) => {
      const index = state.jobs.findIndex((job: TranscriptionJob) => job.id === action.payload.id);
      if (index !== -1) {
        state.jobs[index] = action.payload;
      }
    },
    setProcessingOriginPath: (state, action: PayloadAction<string | null>) => {
      state.processingOriginPath = action.payload;
    },
  },
  extraReducers: (builder) => {
        // fetchQueue
    builder.addCase(fetchQueue.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchQueue.fulfilled, (state, action) => {
      state.loading = false;
      state.jobs = action.payload;
    });
    builder.addCase(fetchQueue.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

        // startQueueProcessing
    builder.addCase(startQueueProcessing.pending, (state) => {
      state.isProcessing = true;
      state.liveOutput = '▶️ Starting queue processing...';
      state.error = null;
    });
    builder.addCase(startQueueProcessing.fulfilled, () => {
            // Keep isProcessing true - it will be set to false by events
    });
    builder.addCase(startQueueProcessing.rejected, (state, action) => {
      state.isProcessing = false;
      state.liveOutput = '';
      state.error = action.payload as string;
    });

        // stopQueueProcessing
    builder.addCase(stopQueueProcessing.fulfilled, (state) => {
      state.isProcessing = false;
      state.liveOutput = '⏸️ Queue processing paused';
      state.processingOriginPath = null;
    });

        // addToQueue
    builder.addCase(addToQueue.fulfilled, (state) => {
      state.queueCompletionStatus = '';
      localStorage.removeItem('queueCompletionStatus');
    });

        // removeFromQueue
    builder.addCase(removeFromQueue.fulfilled, (state) => {
      state.queueCompletionStatus = '';
      localStorage.removeItem('queueCompletionStatus');
    });
  },
});

export const {
  setLiveOutput,
  setQueueCompletionStatus,
  clearCompletionStatus,
  updateJobInQueue,
  setProcessingOriginPath,
} = queueSlice.actions;

export default queueSlice.reducer;
